import { dayjs } from '../../common/lib/dayjs';
export const analyticsNumberFormatter = (count, decimals = 2) => {
    const SI_SYMBOLS = ['', 'K', 'M', 'B', 'T', 'P', 'E', 'Z', 'Y'];
    const countNum = +count;
    if (Number.isNaN(countNum) || !Number.isFinite(countNum))
        return '0';
    const countAbs = Math.abs(countNum);
    if (countAbs > 0 && countAbs < 1)
        return countAbs.toFixed(decimals);
    if (countAbs <= 0)
        return '0';
    const sign = countNum < 0 ? '-' : '';
    let symbolIndex = Math.floor(Math.log(countAbs) / Math.log(1000));
    let result = parseFloat((countAbs / 1000 ** symbolIndex).toFixed(decimals));
    // to handle 999995,6,7,8,9 -> 1000K and 999999995,6,7,8,9 -> 1000M problem
    if (result >= 1000) {
        result = 1;
        symbolIndex += 1;
    }
    const symbol = SI_SYMBOLS[symbolIndex];
    return `${sign}${result}${symbol}`;
};
const parsePayload = (data) => {
    if (!data)
        return {};
    return data === null || data === void 0 ? void 0 : data.reduce((obj, keyValue) => {
        return { ...obj, [keyValue.key]: keyValue.value };
    }, {});
};
export const getValueFromAnalyticsData = (data, key) => {
    var _a, _b, _c;
    if (!data)
        return '';
    return (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.records[0]) === null || _a === void 0 ? void 0 : _a.payload) === null || _b === void 0 ? void 0 : _b.find(p => (p === null || p === void 0 ? void 0 : p.key) === key)) === null || _c === void 0 ? void 0 : _c.value;
};
const mergeAnalyticsData = (obj1, obj2) => {
    const mergedObj = Object.entries(obj1).reduce((output, [key, value]) => {
        var _a;
        return { ...output, [key]: value + ((_a = obj2 === null || obj2 === void 0 ? void 0 : obj2[key]) !== null && _a !== void 0 ? _a : 0) };
    }, obj1);
    return mergedObj;
};
const normalizeDataOnTimeBucket = (data, indexBy) => {
    var _a;
    return (_a = data === null || data === void 0 ? void 0 : data.records) === null || _a === void 0 ? void 0 : _a.reduce((output, record) => {
        var _a, _b, _c, _d;
        const payload = parsePayload(record === null || record === void 0 ? void 0 : record.payload);
        const { timeBucket, ...keyValues } = payload;
        const values = Object.entries(keyValues).reduce((obj, [key, value]) => {
            if (indexBy.indexOf(key) !== -1) {
                let numericValue = +value;
                if (Number.isNaN(numericValue))
                    numericValue = 0;
                return { ...obj, [key]: numericValue };
            }
            return obj;
        }, {});
        // timeBucket returned from server has an unwanted space in the end
        // we use trim() to get rid of spaces to be able to parse the date
        const prevValues = output === null || output === void 0 ? void 0 : output[JSON.parse((_b = (_a = timeBucket) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : '""')];
        const currentValues = prevValues
            ? mergeAnalyticsData(values, prevValues)
            : values;
        return {
            ...output,
            [JSON.parse((_d = (_c = timeBucket) === null || _c === void 0 ? void 0 : _c.trim()) !== null && _d !== void 0 ? _d : '""')]: currentValues,
        };
    }, {});
};
const parseLineChartData = (normalizedData, indexBy, timeFrame) => {
    const allZeroObj = indexBy.reduce((obj, label) => {
        return { ...obj, [label]: 0 };
    }, {});
    let parsedData;
    const startDate = (timeFrame === null || timeFrame === void 0 ? void 0 : timeFrame.to) ? dayjs(timeFrame.to) : dayjs();
    if (timeFrame.id === 'allTime') {
        parsedData = Object.keys(normalizedData !== null && normalizedData !== void 0 ? normalizedData : {}).map(timeBucket => {
            var _a;
            const time = dayjs(timeBucket);
            const dateString = time.format('MMM DD YYYY');
            return {
                date: dateString,
                ...allZeroObj,
                ...((_a = normalizedData === null || normalizedData === void 0 ? void 0 : normalizedData[timeBucket]) !== null && _a !== void 0 ? _a : {}),
            };
        });
        return parsedData;
    }
    switch (timeFrame.granularity) {
        case 'day':
            parsedData = [...Array(timeFrame.xAxisCount)].map((_, index) => {
                var _a;
                const time = startDate.subtract(timeFrame.xAxisCount - index - 1, timeFrame.granularity);
                const timeBucketToFind = time.format('YYYY-MM-DD');
                const dateString = time.format('ddd MMM DD');
                return {
                    date: dateString,
                    ...allZeroObj,
                    ...((_a = normalizedData === null || normalizedData === void 0 ? void 0 : normalizedData[timeBucketToFind]) !== null && _a !== void 0 ? _a : {}),
                };
            });
            break;
        case 'month':
            parsedData = [...Array(timeFrame.xAxisCount)].map((_, index) => {
                var _a;
                const time = startDate
                    .startOf('month')
                    .subtract(timeFrame.xAxisCount - index - 1, timeFrame.granularity);
                const timeBucketToFind = time.format('M');
                const dateString = time.format('MMM YYYY');
                return {
                    date: dateString,
                    ...allZeroObj,
                    ...((_a = normalizedData === null || normalizedData === void 0 ? void 0 : normalizedData[timeBucketToFind]) !== null && _a !== void 0 ? _a : {}),
                };
            });
            break;
        case 'dayOfWeek':
            parsedData = [...Array(timeFrame.xAxisCount)].map((_, index) => {
                var _a;
                const time = startDate.day(index + 1);
                const timeBucketToFind = index + 1;
                const dateString = time.format('ddd');
                return {
                    date: dateString,
                    ...allZeroObj,
                    ...((_a = normalizedData === null || normalizedData === void 0 ? void 0 : normalizedData[timeBucketToFind]) !== null && _a !== void 0 ? _a : {}),
                };
            });
            break;
        case 'hour':
            parsedData = [...Array(timeFrame.xAxisCount)].map((_, index) => {
                var _a;
                const time = startDate.hour(index);
                const timeBucketToFind = index;
                const dateString = `${time.format('HH')}:00`;
                return {
                    date: dateString,
                    ...allZeroObj,
                    ...((_a = normalizedData === null || normalizedData === void 0 ? void 0 : normalizedData[timeBucketToFind]) !== null && _a !== void 0 ? _a : {}),
                };
            });
            break;
        default:
            // Code should never reach the default case
            // eslint-disable-next-line no-case-declarations
            const exhaustiveCheck = timeFrame.granularity;
            return exhaustiveCheck;
    }
    return parsedData;
};
const groupAnalyticsData = (data, groupBy) => {
    let output = {};
    groupBy.forEach(({ indexBy, field, values, as }) => {
        var _a;
        (_a = data === null || data === void 0 ? void 0 : data.records) === null || _a === void 0 ? void 0 : _a.forEach(record => {
            var _a, _b, _c, _d, _e, _f;
            const payload = parsePayload(record === null || record === void 0 ? void 0 : record.payload);
            const fieldValue = JSON.parse((_a = payload === null || payload === void 0 ? void 0 : payload[field]) !== null && _a !== void 0 ? _a : '""');
            let indexValue = +(payload === null || payload === void 0 ? void 0 : payload[indexBy]);
            if (Number.isNaN(indexValue))
                indexValue = 0;
            const timeBucket = JSON.parse((_c = (_b = payload === null || payload === void 0 ? void 0 : payload.timeBucket) === null || _b === void 0 ? void 0 : _b.trim()) !== null && _c !== void 0 ? _c : '""');
            const prevTimeBucketObj = (_d = output === null || output === void 0 ? void 0 : output[timeBucket]) !== null && _d !== void 0 ? _d : {};
            const prevValue = (_f = (_e = output === null || output === void 0 ? void 0 : output[timeBucket]) === null || _e === void 0 ? void 0 : _e[as]) !== null && _f !== void 0 ? _f : 0;
            if (values.indexOf(fieldValue) !== -1) {
                output = {
                    ...output,
                    [timeBucket]: { ...prevTimeBucketObj, [as]: prevValue + indexValue },
                };
            }
        });
    });
    return output;
};
export const rechartsLineChartParser = (data, indexBy, timeFrame, groupBy) => {
    const normalizedData = groupBy
        ? groupAnalyticsData(data, groupBy)
        : normalizeDataOnTimeBucket(data, indexBy);
    return parseLineChartData(normalizedData, indexBy, timeFrame);
};
export const rechartsCumulativeAreaChartParser = (data, indexBy, timeFrame, groupBy) => {
    const parsedData = rechartsLineChartParser(data, indexBy, timeFrame, groupBy);
    for (let i = 1; i < parsedData.length; i++) {
        const keys = Object.keys(parsedData[i]);
        keys.forEach(key => {
            if (indexBy.indexOf(key) !== -1)
                parsedData[i][key] += parsedData[i - 1][key];
        });
    }
    return parsedData;
};
const groupPieChartData = (data, groupBy) => {
    return groupBy.map(({ indexBy, field, values, as }) => {
        var _a;
        let output = { label: as, value: 0 };
        (_a = data === null || data === void 0 ? void 0 : data.records) === null || _a === void 0 ? void 0 : _a.forEach(record => {
            var _a, _b;
            const payload = parsePayload(record === null || record === void 0 ? void 0 : record.payload);
            const fieldValue = JSON.parse((_a = payload === null || payload === void 0 ? void 0 : payload[field]) !== null && _a !== void 0 ? _a : '""');
            let indexValue = +(payload === null || payload === void 0 ? void 0 : payload[indexBy]);
            if (Number.isNaN(indexValue) || !Number.isFinite(indexValue))
                indexValue = 0;
            const prevValue = (_b = output === null || output === void 0 ? void 0 : output.value) !== null && _b !== void 0 ? _b : 0;
            if (values.indexOf(fieldValue) !== -1) {
                output = {
                    ...output,
                    value: prevValue + indexValue,
                };
            }
        });
        return output;
    });
};
const normalizePieChartData = (data, indexBy) => {
    return indexBy.map(label => {
        let value = +getValueFromAnalyticsData(data, label);
        if (Number.isNaN(value) || !Number.isFinite(value)) {
            value = 0;
        }
        return { label, value };
    });
};
export const removeNegativeValues = parsedData => {
    return parsedData.map(data => {
        const value = Math.max(0, data.value);
        return { ...data, value };
    });
};
export const rechartsPieChartParser = (data, indexBy, groupBy) => {
    const parsedData = groupBy
        ? groupPieChartData(data, groupBy)
        : normalizePieChartData(data, indexBy);
    return removeNegativeValues(parsedData);
};
export const normalizeListData = (data) => {
    var _a;
    if (!data)
        return [];
    return (_a = data === null || data === void 0 ? void 0 : data.records) === null || _a === void 0 ? void 0 : _a.reduce((output, record) => {
        var _a;
        const payload = parsePayload(record === null || record === void 0 ? void 0 : record.payload);
        const entities = (_a = record === null || record === void 0 ? void 0 : record.entities) !== null && _a !== void 0 ? _a : {};
        return [...output, { payload, entities }];
    }, []);
};
//# sourceMappingURL=parser.js.map